/**
 * Company React Query Hooks
 *
 * React Query hooks for company CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import type {
  Company,
  CreateCompanyDto,
  UpdateCompanyDto,
  CompanyStatus,
  CompanyHierarchyNode,
  PaginatedResponse,
} from '@lognai/plugin-admin';
import * as companyApi from '../api/companies';
import type { CompanyQueryParams, VisibleCompanyQueryParams } from '../api/companies';
import { companyKeys } from './query-keys';

/**
 * Get list of companies with pagination
 */
export const useCompanies = (
  params?: CompanyQueryParams,
  options?: Omit<UseQueryOptions<PaginatedResponse<Company>>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: companyKeys.list(params),
    queryFn: () => companyApi.listCompanies(params),
    ...options,
  });
};

/**
 * Get visible companies (own + partners + children)
 * Returns companies the current user has access to based on their company context
 */
export const useVisibleCompanies = (
  params?: VisibleCompanyQueryParams,
  options?: Omit<UseQueryOptions<PaginatedResponse<Company>>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: companyKeys.visibleList(params),
    queryFn: () => companyApi.listVisibleCompanies(params),
    ...options,
  });
};

/**
 * Get root companies
 */
export const useRootCompanies = (
  options?: Omit<UseQueryOptions<Company[]>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: companyKeys.roots(),
    queryFn: () => companyApi.getRootCompanies(),
    ...options,
  });
};

/**
 * Get a single company by ID
 */
export const useCompany = (
  id: string,
  options?: Omit<UseQueryOptions<Company>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: companyKeys.detail(id),
    queryFn: () => companyApi.getCompany(id),
    enabled: !!id,
    ...options,
  });
};

/**
 * Get company by slug
 */
export const useCompanyBySlug = (
  slug: string,
  options?: Omit<UseQueryOptions<Company>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: companyKeys.bySlug(slug),
    queryFn: () => companyApi.getCompanyBySlug(slug),
    enabled: !!slug,
    ...options,
  });
};

/**
 * Get company hierarchy
 */
export const useCompanyHierarchy = (
  id: string,
  options?: Omit<UseQueryOptions<CompanyHierarchyNode>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: companyKeys.hierarchy(id),
    queryFn: () => companyApi.getCompanyHierarchy(id),
    enabled: !!id,
    ...options,
  });
};

/**
 * Create a new company
 */
export const useCreateCompany = (
  options?: Omit<UseMutationOptions<Company, Error, CreateCompanyDto>, 'mutationFn' | 'onSuccess'> & {
    onSuccess?: (data: Company, variables: CreateCompanyDto, context: unknown) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: companyApi.createCompany,
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: companyKeys.roots() });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};

/**
 * Update an existing company
 */
export const useUpdateCompany = (
  options?: Omit<
    UseMutationOptions<Company, Error, { id: string; dto: UpdateCompanyDto }>,
    'mutationFn' | 'onSuccess'
  > & {
    onSuccess?: (
      data: Company,
      variables: { id: string; dto: UpdateCompanyDto },
      context: unknown
    ) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }) => companyApi.updateCompany(id, dto),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: companyKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};

/**
 * Update company status
 */
export const useUpdateCompanyStatus = (
  options?: Omit<
    UseMutationOptions<Company, Error, { id: string; status: CompanyStatus }>,
    'mutationFn' | 'onSuccess'
  > & {
    onSuccess?: (
      data: Company,
      variables: { id: string; status: CompanyStatus },
      context: unknown
    ) => void;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }) => companyApi.updateCompanyStatus(id, status),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({ queryKey: companyKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: companyKeys.lists() });
      options?.onSuccess?.(data, variables, context);
    },
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};
